/**
 * Which navigation entry to highlight while the settings scroll: the first
 * section (in page order) crossing the reading band, the last one once the
 * page is scrolled to its end, otherwise whatever was active before.
 */
export function pickActiveSection(
  order: readonly string[],
  visible: ReadonlySet<string>,
  current: string,
  atEnd: boolean
): string {
  if (atEnd && order.length) return order[order.length - 1];
  return order.find((section) => visible.has(section)) ?? current;
}

/**
 * Tracks the highlighted section from visibility reports and navigation
 * clicks. A clicked section stays highlighted while the page travels to it:
 * until it becomes the natural pick, the scroll settles, or the lock expires.
 * DOM-free so the rules can be unit-tested; the composable feeds it events.
 */
export class SettingsSectionTracker {
  active: string;
  private readonly visible = new Set<string>();
  private chosen: string | null = null;
  private lockUntil = 0;

  constructor(
    private readonly order: readonly string[],
    private readonly now: () => number = Date.now
  ) {
    this.active = order[0] ?? '';
  }

  setVisible(section: string, visible: boolean) {
    if (visible) this.visible.add(section);
    else this.visible.delete(section);
  }

  /** Forgets every visibility report and any pending choice. */
  reset() {
    this.visible.clear();
    this.chosen = null;
  }

  choose(section: string, lockMs: number) {
    this.chosen = section;
    this.lockUntil = this.now() + lockMs;
    this.active = section;
  }

  /** Recomputes the highlight after a scroll or visibility change. */
  update(atEnd: boolean): string {
    const picked = pickActiveSection(
      this.order,
      this.visible,
      this.active,
      atEnd
    );
    if (this.chosen) {
      if (picked !== this.chosen && this.now() < this.lockUntil)
        return this.active;
      this.chosen = null;
    }
    this.active = picked;
    return this.active;
  }

  /**
   * The scroll stopped after a choice: a chosen section still on screen keeps
   * its highlight (short last sections never reach the band); otherwise the
   * reader moved elsewhere and the highlight catches up.
   */
  settle(atEnd: boolean): string {
    const was = this.chosen;
    this.chosen = null;
    if (was && !this.visible.has(was)) return this.update(atEnd);
    return this.active;
  }
}
